"use client";

import type {
  NearbyOfficeOption,
  NearbyOrganization,
} from "@homeinstead/marketing-api";

import { useOfficeSearch as useOfficeSearchQuery } from "@homeinstead/geolocation/hooks";
import { mapNearbyOrganizationsToOfficeOptions } from "@homeinstead/marketing-api";
import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { createOfficeSearchMachine } from "../utils/create-office-search-machine";
import { isPostalCodeFormatValid } from "../utils/is-postal-code-format-valid";

type OfficeSearchInitialState = {
  /** A restored office to prefer once the first lookup returns. */
  preferredOfficeId: number | null;
  /** That office's postal code, when the franchise has more than one. */
  preferredPostalCode: string | null;
  typedLocation: string;
};

type UseOfficeSearchInput = {
  /**
   * Read once, when the search starts: after that the search owns typing
   * and selection, and a restored office is only a preference.
   */
  getInitialState: () => OfficeSearchInitialState;
  /** False skips the office lookup when the step is inert. */
  isLookupEnabled?: boolean;
};

/**
 * The postal code question's search: what the visitor typed, the office
 * lookup for a well-formed code, and the office picked from its results.
 * Callers decide how the selection is stored.
 */
function useOfficeSearch({
  getInitialState,
  isLookupEnabled = true,
}: UseOfficeSearchInput) {
  const { context: localeContext } = useLocale();
  const [machine] = useState(() =>
    createOfficeSearchMachine(getInitialState()),
  );
  const [snapshot, send] = useMachine(machine);
  const { preferredOfficeId, selectedOptionId, typedLocation } =
    snapshot.context;
  const isFormatValid = isPostalCodeFormatValid(
    typedLocation,
    localeContext.code,
  );

  const {
    data: officeSearch,
    error: lookupError,
    isFetching,
  } = useOfficeSearchQuery({
    enabled: isFormatValid && isLookupEnabled,
    location: typedLocation,
  });
  const hasLookupError = Boolean(lookupError);

  const officeOptions = useMemo(
    () =>
      hasLookupError
        ? []
        : mapNearbyOrganizationsToOfficeOptions(
            (officeSearch?.organizations ?? []) as NearbyOrganization[],
          ),
    [hasLookupError, officeSearch?.organizations],
  );

  useEffect(() => {
    send({
      hasLookupError,
      isFetching,
      options: officeOptions,
      type: "SYNC_LOOKUP",
    });
  }, [hasLookupError, isFetching, officeOptions, send]);

  const typeLocation = useCallback(
    (value: string) => send({ type: "TYPE", value }),
    [send],
  );

  /**
   * Picks an office from the current results. An id outside them, such as
   * a stale value sent while the options swap, is ignored so it can't clear
   * a valid selection.
   */
  const selectOffice = useCallback(
    (optionId: string): NearbyOfficeOption | undefined => {
      const option = officeOptions.find(
        (officeOption) => officeOption.optionId === optionId,
      );
      if (option) {
        send({ optionId, type: "SELECT_OPTION" });
      }
      return option;
    },
    [officeOptions, send],
  );

  return {
    hasLookupError,
    isFetching,
    isFormatValid,
    /** A restored office waits for the lookup that can confirm it. */
    isRestorePending: preferredOfficeId != null,
    localeCode: localeContext.code,
    officeOptions,
    selectedOption: snapshot.context.options.find(
      (option) => option.optionId === selectedOptionId,
    ),
    selectedOptionId,
    selectOffice,
    typedLocation,
    typeLocation,
  };
}

export { useOfficeSearch };
